// „Przekrój sprzedaży” settings live in the page address, so a copied link
// opens the same cut (rows, columns, measure, scope, filters, sort). Other query
// parameters (`tab`, the report `period`) are kept as they are.
import type { PivotColumns, PivotDimension, PivotFilters, PivotMeasure, PivotScope, PivotSort } from './orderPivot';

export interface PivotViewState {
  first: PivotDimension;
  second: PivotDimension | null;
  columns: PivotColumns;
  measure: PivotMeasure;
  scope: PivotScope;
  change: boolean;
  filters: PivotFilters;
  /** null = the default order (by value, largest first). */
  sort: PivotSort | null;
}

export const DEFAULT_PIVOT_VIEW: PivotViewState = {
  first: 'manager',
  second: 'customer',
  columns: 'total',
  measure: 'revenue',
  scope: 'all',
  change: false,
  filters: { manager: [], customer: [], product: [], status: [], hideNoCost: false },
  sort: null,
};

const DIMENSIONS: PivotDimension[] = ['manager', 'customer', 'product'];
const COLUMNS: PivotColumns[] = ['total', 'month', 'quarter', 'year'];
const MEASURES: PivotMeasure[] = ['revenue', 'cost', 'margin', 'marginPercent', 'orders'];
const SCOPES: PivotScope[] = ['all', 'actual', 'forecast'];

// Short names in the address; filters repeat the key once per value.
const PARAM = {
  first: 'rows', second: 'then', columns: 'cols', measure: 'measure', scope: 'scope', change: 'change',
  manager: 'manager', customer: 'customer', product: 'product', status: 'status', hideNoCost: 'nocost', sort: 'sort',
} as const;

// `sort=margin` (descending) or `sort=margin.asc`; the key is a column or a bucket.
function readSort(value: string | null): PivotSort | null {
  const match = value ? /^([A-Za-z0-9-]+?)(\.asc)?$/.exec(value) : null;
  return match ? { key: match[1], direction: match[2] ? 'asc' : 'desc' } : null;
}

function oneOf<T extends string>(value: string | null, allowed: T[], fallback: T): T {
  return value && (allowed as string[]).includes(value) ? (value as T) : fallback;
}

export function readPivotView(params: URLSearchParams): PivotViewState {
  const first = oneOf(params.get(PARAM.first), DIMENSIONS, DEFAULT_PIVOT_VIEW.first);
  const rawSecond = params.get(PARAM.second);
  const second = rawSecond === 'none' ? null : oneOf(rawSecond, DIMENSIONS, DEFAULT_PIVOT_VIEW.second!);
  return {
    first,
    second: second === first ? null : second,
    columns: oneOf(params.get(PARAM.columns), COLUMNS, DEFAULT_PIVOT_VIEW.columns),
    measure: oneOf(params.get(PARAM.measure), MEASURES, DEFAULT_PIVOT_VIEW.measure),
    scope: oneOf(params.get(PARAM.scope), SCOPES, DEFAULT_PIVOT_VIEW.scope),
    change: params.get(PARAM.change) === '1',
    filters: {
      manager: params.getAll(PARAM.manager).filter(Boolean),
      customer: params.getAll(PARAM.customer).filter(Boolean),
      product: params.getAll(PARAM.product).filter(Boolean),
      status: params.getAll(PARAM.status).filter(Boolean),
      hideNoCost: params.get(PARAM.hideNoCost) === 'hide',
    },
    sort: readSort(params.get(PARAM.sort)),
  };
}

/** A copy of `params` with the view written in; defaults are left out of the address. */
export function writePivotView(params: URLSearchParams, view: PivotViewState): URLSearchParams {
  const next = new URLSearchParams(params);
  const set = (key: string, value: string, fallback: string) => {
    if (value === fallback) next.delete(key);
    else next.set(key, value);
  };
  set(PARAM.first, view.first, DEFAULT_PIVOT_VIEW.first);
  set(PARAM.second, view.second ?? 'none', DEFAULT_PIVOT_VIEW.second ?? 'none');
  set(PARAM.columns, view.columns, DEFAULT_PIVOT_VIEW.columns);
  set(PARAM.measure, view.measure, DEFAULT_PIVOT_VIEW.measure);
  set(PARAM.scope, view.scope, DEFAULT_PIVOT_VIEW.scope);
  set(PARAM.change, view.change ? '1' : '0', '0');
  set(PARAM.hideNoCost, view.filters.hideNoCost ? 'hide' : '', '');
  set(PARAM.sort, view.sort ? `${view.sort.key}${view.sort.direction === 'asc' ? '.asc' : ''}` : '', '');
  (['manager', 'customer', 'product', 'status'] as const).forEach((dimension) => {
    next.delete(PARAM[dimension]);
    view.filters[dimension].forEach((value) => next.append(PARAM[dimension], value));
  });
  return next;
}

export function activePivotFilterCount(filters: PivotFilters): number {
  return filters.manager.length + filters.customer.length + filters.product.length
    + filters.status.length + (filters.hideNoCost ? 1 : 0);
}
